/**
 * Tool call bubble chrome: "bubble" (full card) vs "simple" (minimal row).
 * Bubble style is tool-authored; mutating-tool fallback list remains only for
 * workspace-refresh logic and older callers that still request it.
 * Function names from models may be kebab-case or snake_case — always canonicalize
 * via {@link AIToolSystem#resolveRegisteredToolName} when available, else `_` → `-`.
 */

/** Registered tool IDs that mutate workspace state (kebab-case keys matching tool registry) */
export const MUTATING_TOOL_IDS_FOR_BUBBLE_STYLE = [
  "search-replace",
  "write-file",
  "create-folder",
  "delete-file",
  "move-file",
  "duplicate-file",
  "set-entry-file-path",
  "write-agent-entry",
  "write-agent-readme",
];

/**
 * `_` → `-` only (no registry). Use when no AIToolSystem is available (e.g. ChatBubble display).
 * @param {string} name
 * @returns {string}
 */
export function snakeToKebabToolName(name) {
  if (!name || typeof name !== "string") return "";
  return name.replace(/_/g, "-");
}

/**
 * Canonical tool id for UI: same as registry key when the tool exists; otherwise snake→kebab.
 * @param {string} functionName
 * @param {import("../base/AIToolSystem.js").AIToolSystem | null | undefined} aiToolSystem
 * @returns {string}
 */
export function canonicalizeToolFunctionName(functionName, aiToolSystem) {
  if (!functionName || typeof functionName !== "string") return "";
  const resolved = aiToolSystem?.resolveRegisteredToolName?.(functionName);
  if (resolved) return resolved;
  return snakeToKebabToolName(functionName);
}

/**
 * Resolve bubble style for a tool call: optional override, then tool.bubbleStyle, then "simple".
 * @param {string} functionName - Raw name from stream or persisted chat
 * @param {import("../base/AIToolSystem.js").AIToolSystem | null | undefined} aiToolSystem
 * @param {((canonicalName: string) => "bubble"|"simple")|null|undefined} getBubbleStyleOverride - receives canonical name
 * @returns {"bubble"|"simple"}
 */
export function resolveToolBubbleStyle(
  functionName,
  aiToolSystem,
  getBubbleStyleOverride
) {
  const canonical = canonicalizeToolFunctionName(functionName, aiToolSystem);
  if (typeof getBubbleStyleOverride === "function") {
    return getBubbleStyleOverride(canonical);
  }
  const toolInstance = aiToolSystem?.getToolInstance?.(functionName, null);
  if (toolInstance?.bubbleStyle) {
    return toolInstance.bubbleStyle;
  }
  return "simple";
}

/**
 * Whether a tool name (after canonicalization) should trigger workspace auto-refresh on success.
 * @param {string} functionName
 * @param {import("../base/AIToolSystem.js").AIToolSystem | null | undefined} aiToolSystem
 * @returns {boolean}
 */
export function isMutatingToolForWorkspaceRefresh(functionName, aiToolSystem) {
  const canonical = canonicalizeToolFunctionName(functionName, aiToolSystem);
  return MUTATING_TOOL_IDS_FOR_BUBBLE_STYLE.includes(canonical);
}
